import { Badge, Spinner } from 'react-bootstrap';
import { CheckCircleFill, XCircleFill, PlayFill, ClockFill, DashCircleFill, ExclamationTriangleFill, ArrowRepeat, ArchiveFill } from 'react-bootstrap-icons';

const STATUS_VARIANT: Record<string, string> = {
  running: 'primary',
  started: 'primary',
  dispatched: 'primary',
  succeeded: 'success',
  completed: 'success',
  finished: 'success',
  connected: 'success',
  failed: 'danger',
  error: 'danger',
  killing: 'killing',
  killed: 'killed',
  canceled: 'warning',
  terminated: 'warning',
  pending: 'secondary',
  scheduled: 'secondary',
  waiting: 'secondary',
  hold: 'secondary',
  initializing: 'secondary',
  disabled: 'secondary',
  deleting: 'secondary',
  skipped: 'secondary',
  WorkflowWait: 'info',
  eventWait: 'info',
  deprecated: 'deprecated',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

interface StatusBadgeProps {
  status: string;
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  if (!status) return null;

  const lower = status.toLowerCase();
  const variant = STATUS_VARIANT[status] ?? STATUS_VARIANT[lower] ?? 'secondary';

  // Extract the base status before any sub-status suffix like "Running (QueueWait)"
  const parenIdx = status.indexOf('(');
  const baseStatus = parenIdx !== -1 ? status.slice(0, parenIdx).trim() : status;
  const subStatus = parenIdx !== -1 ? status.slice(parenIdx) : null;
  const baseLower = baseStatus.toLowerCase();

  let icon: React.ReactNode = null;
  let label = capitalize(baseStatus);

  if (baseLower === 'killing') {
    icon = <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.1em' }} />;
    label = 'Killing';
  } else if (baseLower === 'deleting') {
    icon = <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.1em' }} />;
    label = 'Deleting';
  } else if (baseLower === 'killed') {
    icon = <XCircleFill className="me-1" style={{ fontSize: '0.7rem' }} />;
    label = 'Killed';
  } else if (baseLower === 'failed' || baseLower === 'error') {
    icon = <ExclamationTriangleFill className="me-1" style={{ fontSize: '0.7rem' }} />;
  } else if (baseLower === 'succeeded' || baseLower === 'completed' || baseLower === 'finished') {
    icon = <CheckCircleFill className="me-1" style={{ fontSize: '0.7rem' }} />;
  } else if (baseLower === 'running' || baseLower === 'started' || baseLower === 'dispatched') {
    icon = <PlayFill className="me-1" style={{ fontSize: '0.7rem' }} />;
  } else if (baseLower === 'canceled' || baseLower === 'terminated') {
    icon = <DashCircleFill className="me-1" style={{ fontSize: '0.7rem' }} />;
  } else if (baseLower === 'pending' || baseLower === 'scheduled' || baseLower === 'waiting' || baseLower === 'eventwait') {
    icon = <ClockFill className="me-1" style={{ fontSize: '0.7rem' }} />;
  } else if (baseLower === 'initializing') {
    icon = <ArrowRepeat className="me-1" style={{ fontSize: '0.7rem' }} />;
  } else if (baseLower === 'deprecated') {
    icon = <ArchiveFill className="me-1" style={{ fontSize: '0.7rem' }} />;
    label = 'Deprecated';
  }

  const customColor = variant === 'killed' ? '#c0392b' : variant === 'killing' ? '#e74c3c' : variant === 'deprecated' ? '#e05a00' : null;

  return (
    <>
      {customColor && <style>{`.badge-${variant} { background-color: ${customColor} !important; color: #fff !important; }`}</style>}
      <Badge
        bg={customColor ? 'danger' : variant}
        className={`d-inline-flex align-items-center fw-normal${customColor ? ` badge-${variant}` : ''}`}
        style={{ fontSize: '0.95em', minWidth: '7rem' }}
        text={variant === 'warning' ? 'dark' : undefined}
      >
        {icon}
        {label}
        {subStatus && <span className="ms-1 opacity-75">{subStatus}</span>}
      </Badge>
    </>
  );
};

export default StatusBadge;
